import React, { useCallback, useRef, useState } from 'react';

import { AccountManager, btag, debounce, pushToDataLayer } from '@atlas/core';
import { Icon } from '@atlas/ui';

import useTranslate from 'common/I18n/Interpreter';
import { useCasinoFilterState } from 'components/Pages/Casino/CasinoFilterContext';
import {
  casinoFilterTypeMap,
  deleteCasinoFilter,
  getFilter,
  updateCasinoFilter,
} from 'components/Pages/Casino/useCasinoFilter';

export const MIN_SEARCH_COUNT = 3;

const FilterSearch = ({ isSearchActive, setIsSearchActive }) => {
  const searchInputEl = useRef(null);
  const t = useTranslate();
  const searchQuery = getFilter(casinoFilterTypeMap.name) || '';
  const { updateFilter } = useCasinoFilterState();
  const [val, setVal] = useState(searchQuery);

  const [showMessage, setShowMessage] = useState(!searchQuery.length);
  const onChange = useCallback(
    e => {
      const { value } = e.target;

      if (!value) {
        cleanSearch();
        return;
      }

      setVal(value);
      setShowMessage(!searchQuery && value.length < MIN_SEARCH_COUNT);

      if (
        value.length >= MIN_SEARCH_COUNT ||
        (value.length < MIN_SEARCH_COUNT && val.length === MIN_SEARCH_COUNT)
      ) {
        onChangeHandler();
      }
    },
    [searchQuery, val],
  );

  const onChangeHandler = useCallback(
    debounce(() => {
      const { clientId } = AccountManager.account;
      const { value } = searchInputEl.current;

      updateCasinoFilter(casinoFilterTypeMap.name, value);
      updateFilter({ name: value });
      setShowMessage(false);

      pushToDataLayer(
        'casino_search_request',
        { client: { id: clientId } },
        {
          name: value,
        },
        btag,
      );
    }, 250),
    [],
  );

  const backToCasino = useCallback(() => {
    setIsSearchActive(false);
    updateFilter({ name: '' });
    deleteCasinoFilter(casinoFilterTypeMap.name);
  }, [setIsSearchActive, updateFilter]);

  const cleanSearch = useCallback(() => {
    deleteCasinoFilter(casinoFilterTypeMap.name);
    setVal('');
    setShowMessage(true);
  }, []);

  const onKeyUp = useCallback(
    ({ key }) => {
      if (key === 'Enter') {
        onChangeHandler();
        searchInputEl.current.blur();
      }
    },
    [onChangeHandler],
  );

  return (
    <div className="casino-search search-container">
      <button className="back-to-container ellipsis" onClick={backToCasino}>
        <Icon className="icon-back" name="back" />
      </button>
      <div className="search-box-wrapper">
        <div className="search-box">
          <div className="icon-container">
            <Icon className="icon-search" name="search" />
          </div>
          <label className="input-container">
            <input
              autoFocus
              className="input search-input"
              enterKeyHint="go"
              inputMode="search"
              maxLength="50"
              placeholder={t('search_games')}
              onChange={onChange}
              // will close keyboard on enter
              onKeyUp={onKeyUp}
              ref={searchInputEl}
              // for ios keyboard
              value={val}
            />
            {isSearchActive && val.length > 0 && (
              <div className="icon-container" onClick={cleanSearch}>
                <Icon name="close" />
              </div>
            )}
          </label>
        </div>
        {showMessage && (
          <div className="search-message">{t('casino_search_initial_title_mobile')}</div>
        )}
      </div>
    </div>
  );
};

export default FilterSearch;
